import React, { useCallback, useEffect, useState } from "react";
import { Loader2, RefreshCw, CheckCircle2, Clock, XCircle, Save } from "lucide-react";
import { toast } from "sonner";

import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, unwrap } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { formatDate, formatDateTime } from "@/lib/format";

const STATUS_BADGE = {
  pending_coordination: "bg-amber-50 text-amber-700 border-amber-200",
  coordination_confirmed: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const TIME_SLOT = {
  morning: "Morning (7–11)", midday: "Midday (11–2)", afternoon: "Afternoon (2–5)",
  evening: "Evening (5–8)", night: "Night (8–10)", flexible: "Flexible",
};

function sinceBadge(createdAt) {
  const hrs = (Date.now() - new Date(createdAt).getTime()) / 36e5;
  if (hrs < 12) return { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: `${Math.max(0, Math.round(hrs))}h ago` };
  if (hrs < 24) return { cls: "bg-amber-50 text-amber-700 border-amber-200", label: `${Math.round(hrs)}h ago` };
  const d = Math.floor(hrs / 24);
  return { cls: "bg-rose-50 text-rose-700 border-rose-200", label: d >= 1 ? `${d}d ago` : `${Math.round(hrs)}h ago` };
}

export default function AdminCoordination() {
  const { locale } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // booking in modal

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/coordination");
      setItems(unwrap(r).items || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5" data-testid="admin-coordination">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tighter">Coordination</h1>
          <p className="text-[#4B5563] text-sm mt-1">{items.length} booking(s) awaiting manual coordination</p>
        </div>
        <Button onClick={load} variant="outline" className="rounded-xl" data-testid="coordination-refresh">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      <Card className="rounded-2xl border border-[#E5E7EB] overflow-hidden">
        {loading ? (
          <div className="grid place-items-center py-16"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-[#4B5563]">No coordination requests right now.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="coordination-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-[#4B5563]">
                  <th className="px-5 py-3">#</th>
                  <th className="px-5 py-3">Customer</th>
                  <th className="px-5 py-3">Location</th>
                  <th className="px-5 py-3">Property</th>
                  <th className="px-5 py-3">Preferred</th>
                  <th className="px-5 py-3">Total</th>
                  <th className="px-5 py-3">Age</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((b) => {
                  const age = sinceBadge(b.created_at);
                  return (
                    <tr key={b.id} className="border-t border-[#E5E7EB] hover:bg-[#FAFAF9]" data-testid={`coordination-row-${b.booking_number}`}>
                      <td className="px-5 py-3 font-mono">{b.booking_number}</td>
                      <td className="px-5 py-3">
                        {b.customer.full_name}
                        <div className="text-xs text-[#9CA3AF]">{b.customer.email} · {b.customer.phone}</div>
                      </td>
                      <td className="px-5 py-3">{b.customer.city}<div className="text-xs text-[#9CA3AF]">{b.customer.country}</div></td>
                      <td className="px-5 py-3 capitalize">{b.service.property_type} · {b.service.square_feet}sqft</td>
                      <td className="px-5 py-3">{b.service.preferred_date ? formatDate(b.service.preferred_date, locale) : "—"}<div className="text-xs text-[#9CA3AF]">{TIME_SLOT[b.service.preferred_time_slot] || "—"}</div></td>
                      <td className="px-5 py-3 font-mono font-semibold">${b.pricing.total.toFixed(2)}</td>
                      <td className="px-5 py-3"><Badge className={`border ${age.cls} font-medium`} data-testid={`coordination-age-${b.booking_number}`}>{age.label}</Badge></td>
                      <td className="px-5 py-3"><Badge className={`border ${STATUS_BADGE[b.status]} font-medium capitalize`}>{b.status.replace(/_/g, " ")}</Badge></td>
                      <td className="px-5 py-3">
                        <Button size="sm" variant="outline" className="rounded-xl" onClick={() => setActive(b)} data-testid={`coordination-open-${b.booking_number}`}>Open</Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <CoordinationDetail key={active?.id || "none"} booking={active} onClose={() => setActive(null)} onChanged={() => { setActive(null); load(); }} locale={locale} />
    </div>
  );
}

function CoordinationDetail({ booking, onClose, onChanged, locale }) {
  const [cleaner, setCleaner] = useState(() => booking?.assigned_cleaner || "");
  const [contact, setContact] = useState(() => booking?.cleaner_contact || "");
  const [notes, setNotes] = useState(() => booking?.assignment_notes || "");
  const [savingNotes, setSavingNotes] = useState(false);
  const [busy, setBusy] = useState("");

  if (!booking) return null;
  const s = booking.service, c = booking.customer, p = booking.pricing;

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.patch(`/admin/bookings/${booking.id}/assignment`, {
        assigned_cleaner: cleaner, cleaner_contact: contact, assignment_notes: notes,
      });
      toast.success("Coordination notes saved");
    } catch { toast.error("Could not save"); } finally { setSavingNotes(false); }
  };

  const act = async (kind) => {
    setBusy(kind);
    try {
      // persist notes/assignment first so nothing is lost
      await api.patch(`/admin/bookings/${booking.id}/assignment`, {
        assigned_cleaner: cleaner, cleaner_contact: contact, assignment_notes: notes,
      }).catch(() => {});
      if (kind === "confirm") {
        await api.post(`/admin/coordination/${booking.id}/confirm`);
        toast.success("Confirmed — payment link sent to customer");
      } else if (kind === "need-more-time") {
        await api.post(`/admin/coordination/${booking.id}/need-more-time`);
        toast.success("Update email sent to customer");
      } else if (kind === "cancel") {
        if (!window.confirm("Cancel coordination and notify the customer?")) { setBusy(""); return; }
        await api.post(`/admin/coordination/${booking.id}/cancel`);
        toast.success("Coordination cancelled — customer notified");
      }
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || "Action failed");
    } finally { setBusy(""); }
  };

  return (
    <Dialog open={!!booking} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="coordination-detail-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tighter flex items-center gap-2">
            {booking.booking_number}
            <Badge className={`border ${STATUS_BADGE[booking.status]} font-medium capitalize`}>{booking.status.replace(/_/g, " ")}</Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div className="rounded-xl border border-[#E5E7EB] p-4">
            <div className="font-bold mb-2">Customer</div>
            <div>{c.full_name}</div>
            <div className="text-[#4B5563]">{c.email}</div>
            <div className="text-[#4B5563]">{c.phone}</div>
            <div className="text-[#4B5563] mt-1">{c.address_line1}{c.address_line2 ? `, ${c.address_line2}` : ""}, {c.city} {c.zip_code}</div>
            <div className="text-[#4B5563]">{c.country}</div>
          </div>
          <div className="rounded-xl border border-[#E5E7EB] p-4">
            <div className="font-bold mb-2">Service</div>
            <div className="capitalize">{s.property_type} · {s.square_feet} sq ft</div>
            {s.property_type === "other" && s.space_description ? (
              <div className="mt-1 rounded-lg bg-amber-50 border border-amber-200 px-2.5 py-1.5 text-amber-900" data-testid="coord-space-description">
                <span className="font-semibold">Space type:</span> {s.space_description}
              </div>
            ) : (
              <div className="text-[#4B5563]">{s.bedrooms} BR · {s.bathrooms} BA · {s.half_bathrooms} half</div>
            )}
            <div className="text-[#4B5563] mt-1">Tasks: {(s.tasks || []).join(", ") || "—"}</div>
            <div className="text-[#4B5563]">Preferred: {s.preferred_date ? formatDate(s.preferred_date, locale) : "—"} · {TIME_SLOT[s.preferred_time_slot] || "—"}</div>
            <div className="mt-2 font-mono font-bold text-[#0EA5A4] text-lg">${p.total.toFixed(2)} <span className="text-xs text-[#4B5563]">to charge</span></div>
          </div>
        </div>
        {s.additional_notes && (
          <div className="rounded-xl bg-[#F5F5F4] p-3 text-sm"><span className="text-[#4B5563]">Customer notes: </span>{s.additional_notes}</div>
        )}

        {/* Coordination notes */}
        <div className="rounded-xl border border-[#E5E7EB] p-4 space-y-3">
          <div className="font-bold">Coordination notes</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Assigned cleaner name + contact</Label>
              <Input value={cleaner} onChange={(e) => setCleaner(e.target.value)} placeholder="Cleaner name" className="rounded-xl mt-1" data-testid="coord-cleaner-name" />
            </div>
            <div>
              <Label className="text-xs">Cleaner phone / contact</Label>
              <Input value={contact} onChange={(e) => setContact(e.target.value)} placeholder="Phone / WhatsApp" className="rounded-xl mt-1" data-testid="coord-cleaner-contact" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Internal notes</Label>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Cleaner number, negotiated price, anything…" className="rounded-xl mt-1" data-testid="coord-internal-notes" />
          </div>
          <Button onClick={saveNotes} disabled={savingNotes} variant="outline" size="sm" className="rounded-xl" data-testid="coord-save-notes">
            {savingNotes ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Save notes</>}
          </Button>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-2 pt-2">
          <Button onClick={() => act("confirm")} disabled={!!busy}
            className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white flex-1" data-testid="coord-confirm-btn">
            {busy === "confirm" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-2" /> Confirm and send payment link</>}
          </Button>
          <Button onClick={() => act("need-more-time")} disabled={!!busy}
            className="rounded-xl bg-amber-500 hover:bg-amber-600 text-white flex-1" data-testid="coord-more-time-btn">
            {busy === "need-more-time" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Clock className="w-4 h-4 mr-2" /> Need more time</>}
          </Button>
          <Button onClick={() => act("cancel")} disabled={!!busy}
            variant="outline" className="rounded-xl text-rose-600 hover:bg-rose-50 border-rose-200 flex-1" data-testid="coord-cancel-btn">
            {busy === "cancel" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><XCircle className="w-4 h-4 mr-2" /> Cancel coordination</>}
          </Button>
        </div>
        <div className="text-xs text-[#9CA3AF] text-center">Created {formatDateTime(booking.created_at, locale)}</div>
      </DialogContent>
    </Dialog>
  );
}
